import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
  styled,
  Slide,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useChat } from '../models/ChatContext';
import { handleEvent } from '../controllers/ChatController';
import ReactMarkdown from 'react-markdown';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ChatContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
  backdropFilter: 'blur(40px)',
  WebkitBackdropFilter: 'blur(40px)',
  border: '1px solid rgba(148, 163, 184, 0.1)',
  borderRadius: '20px',
  boxShadow: `
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1)
  `,
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.6) 20%, rgba(118, 75, 162, 0.6) 80%, transparent 100%)',
  },
}));

const ChatHeader = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  color: 'white',
  padding: '16px 24px',
  borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  height: '400px',
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(102, 126, 234, 0.6)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(102, 126, 234, 0.8)',
    },
  },
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  maxWidth: '80%',
  padding: '12px 16px',
  borderRadius: '16px',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  background: isUser 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'white',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  '& p': {
    margin: 0,
    lineHeight: 1.4,
  },
  '& code': {
    background: 'rgba(0, 0, 0, 0.2)',
    padding: '2px 4px',
    borderRadius: '4px',
    fontSize: '0.9em',
  },
  '& pre': {
    background: 'rgba(0, 0, 0, 0.2)',
    padding: '8px',
    borderRadius: '8px',
    overflow: 'auto',
    margin: '8px 0',
  },
}));

const InputContainer = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  gap: '12px',
}));

const ChatOverlay = () => {
  const {
    chatVisible,
    toggleChatVisibility,
    responseTexts,
    setResponseTexts,
    loading,
    userMessage,
    setUserMessage,
    dataChannelRef,
    messagesEndRef,
    audioRef,
    clearChat,
    chatActive,
  } = useChat();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [responseTexts]);

  const handleSendMessage = () => {
    if (userMessage.trim() !== '') {
      setResponseTexts((prevTexts) => [
        ...prevTexts,
        { message: userMessage, type: 'user', isTranscribing: false }
      ]);
      
      // Envoyer le message à ChatGPT via handleEvent
      handleEvent(
        { type: 'user_message', content: userMessage },
        setResponseTexts,
        dataChannelRef
      );
      setUserMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <audio ref={audioRef} autoPlay />
      <Dialog
        open={chatVisible}
        TransitionComponent={Transition}
        keepMounted
        onClose={toggleChatVisibility}
        maxWidth="sm"
        fullWidth
        PaperComponent={ChatContainer}
        sx={{
          '& .MuiDialog-paper': {
            margin: '16px',
            maxHeight: 'calc(100vh - 32px)',
          },
        }}
      >
        <ChatHeader>
          <SmartToyIcon sx={{ color: '#667eea', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Jarvis {chatActive && <span style={{ color: '#10b981' }}>• En ligne</span>}
          </Typography>
          <IconButton
            onClick={clearChat}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <Typography variant="caption">Effacer</Typography>
          </IconButton>
          <IconButton
            onClick={toggleChatVisibility}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </ChatHeader>

        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '400px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Typography>Connexion au modèle en cours...</Typography>
            </Box>
          ) : (
            <MessagesContainer>
              {responseTexts.length === 0 && (
                <Box sx={{ 
                  textAlign: 'center', 
                  color: 'rgba(255, 255, 255, 0.6)',
                  py: 4
                }}>
                  <SmartToyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1">
                    Bonjour ! Je suis Jarvis, votre assistant IA.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {chatActive 
                      ? "Vous pouvez me parler directement ou taper votre message."
                      : "Activez la conversation vocale avec le bouton flottant ou tapez votre message."
                    }
                  </Typography>
                </Box>
              )}
              
              {responseTexts.map((textObj, index) => (
                <MessageBubble key={index} isUser={textObj.type === 'user'}>
                  {typeof textObj.message === 'string' ? (
                    <ReactMarkdown>{textObj.message}</ReactMarkdown>
                  ) : (
                    textObj.message
                  )}
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          )}
        </DialogContent>

        <InputContainer>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Tapez votre message..."
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            variant="outlined"
            InputProps={{
              sx: {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(102, 126, 234, 0.5)',
                },
                '& input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!userMessage.trim()}
            sx={{
              minWidth: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <SendIcon />
          </Button>
        </InputContainer>
      </Dialog>
    </>
  );
};

export default ChatOverlay; 